import { useEffect } from "react";
import { X, Download, FileJson, AlertTriangle } from "lucide-react";
import { DocBlocks } from "@/components/legal/DocBlocks";

// Displays a generated (program preview) or accepted (snapshot) enrollment agreement.
export default function AgreementViewer({ view, onClose, onDownload }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!view) return null;
  const { mode, title, agreements = [], missing = [], advisories = [], registrationId, summary, moves = [] } = view;
  return (
    <div className="fixed inset-0 z-[85] flex items-start justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="agreement-viewer" className="relative w-full max-w-3xl my-8 bg-[#0B0F19] border border-white/10 rounded-3xl p-6 md:p-9">
        <button data-testid="agreement-viewer-close" onClick={onClose} className="absolute top-5 right-5 text-white/50 hover:text-white"><X size={22} /></button>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60a5fa]">{mode === "preview" ? "Agreement Preview · Generated from program settings" : "Accepted Agreement · Immutable snapshot"}</p>
        <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-white pr-8">{title}</h2>

        {mode === "preview" && missing.length > 0 && (
          <p data-testid="agreement-missing" className="mt-4 flex gap-2 rounded-2xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm font-semibold text-red-300"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> Missing required details: {missing.join(", ")}. Registration cannot be opened until these are set.</p>
        )}
        {mode === "preview" && advisories.map((a, i) => (
          <p key={i} className="mt-2 rounded-2xl bg-amber-500/10 border border-amber-500/25 px-4 py-2.5 text-xs font-semibold text-amber-200">{a}</p>
        ))}
        {mode === "record" && summary && (
          <div data-testid="agreement-record-summary" className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 rounded-2xl bg-white/5 border border-white/10 p-4 text-sm">
            {summary.map(([k, v]) => (
              <div key={k}><p className="text-[11px] font-bold uppercase tracking-wider text-white/35">{k}</p><p className="text-white/85 font-semibold break-words">{v || "—"}</p></div>
            ))}
          </div>
        )}
        {moves.map((m, i) => (
          <p key={i} className="mt-3 text-xs font-bold text-amber-300">Moved by admin to {m.to_program_title} on {new Date(m.moved_at).toLocaleString()}. The agreement below reflects the program originally accepted.</p>
        ))}
        {mode === "record" && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="agreement-download-html" onClick={() => onDownload(registrationId, "html")} className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#2563EB] hover:bg-[#1d4fd7] rounded-full px-4 py-2 transition-colors"><Download size={13} /> Download Record (printable)</button>
            <button data-testid="agreement-download-json" onClick={() => onDownload(registrationId, "json")} className="inline-flex items-center gap-1.5 text-xs font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-4 py-2 transition-colors"><FileJson size={13} /> Export JSON</button>
          </div>
        )}

        {agreements.map((a, i) => (
          <div key={i} data-testid={`agreement-doc-${i}`} className="mt-6 rounded-2xl border border-white/10 bg-black/30 px-5 py-4">
            {(a.header || []).map((h, j) => <p key={j} className={j === 1 ? "text-lg font-extrabold text-white" : "text-[11px] font-bold uppercase tracking-wider text-white/40"}>{h}</p>)}
            <p className="mt-1 text-[11px] font-semibold text-white/35">Template v{a.template?.version}{a.agreement_id ? ` · Agreement ID ${a.agreement_id}` : ""}{a.accepted_at ? ` · Accepted ${new Date(a.accepted_at).toLocaleString()}` : ""}</p>
            <DocBlocks blocks={a.blocks} compact emptyText={mode === "preview" ? "Entered / recorded at registration" : "—"} />
          </div>
        ))}
      </div>
    </div>
  );
}
